import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Shield, TrendingUp, Star, AlertTriangle, FileText,
  Loader2, RefreshCw, ChevronDown, ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvestorMemoPanel({ leadId, opportunityId }) {
  const [memo, setMemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMemo = () => {
    if (!leadId) { setLoading(false); return; }
    axios.get(`${API}/admin/agents/deal/${leadId}/outputs`, { headers })
      .then(r => {
        const agents = r.data.agents || {};
        const memoAgent = agents.investor_memo_agent;
        if (memoAgent?.output?.primary_outputs) {
          setMemo(memoAgent.output);
        }
      })
      .catch(() => {})
      .finally(() => { setLoading(false); setRegenerating(false); });
  };

  useEffect(() => { fetchMemo(); }, [leadId]);

  const regenerate = async () => {
    if (!leadId) return;
    setRegenerating(true);
    try {
      await axios.post(`${API}/admin/agents/run-single`, {
        agent_name: "investor_memo_agent",
        lead_id: leadId,
      }, { headers });
      setTimeout(fetchMemo, 1000);
    } catch {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-6 flex items-center justify-center" data-testid="memo-loading">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!memo) {
    return (
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-6 text-center" data-testid="memo-empty">
        <FileText className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
        <p className="text-sm text-[#888] mb-3">Investor memo not yet generated</p>
        <button
          onClick={regenerate}
          disabled={regenerating}
          className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors"
          data-testid="generate-memo-btn"
        >
          {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> : null}
          Generate Investor Memo
        </button>
      </div>
    );
  }

  const outputs = memo.primary_outputs || {};
  const investorMemo = outputs.investor_memo || {};
  const sponsorSummary = outputs.sponsor_summary || {};
  const highlights = outputs.opportunity_highlights || [];
  const protections = outputs.downside_protection || [];
  const confidence = memo.confidence_score || 0;

  return (
    <div className="space-y-4" data-testid="investor-memo-panel">
      {/* Header */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-md">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#222]">AI Investor Memo</h3>
              <p className="text-[9px] text-[#999] uppercase tracking-wider">
                Confidence: {confidence}% | Generated by AI
              </p>
            </div>
          </div>
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="h-7 w-7 rounded-lg bg-[#FDFCF9] border border-[#EDE8DC] flex items-center justify-center text-[#999] hover:text-indigo-600 hover:border-indigo-200 transition-all"
            data-testid="regenerate-memo-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Memo summary */}
        {investorMemo.title && (
          <div className="mb-3">
            <h4 className="text-base font-bold text-[#111]">{investorMemo.title}</h4>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              {investorMemo.property && <div className="text-[#666]"><span className="text-[#999]">Property:</span> {investorMemo.property}</div>}
              {investorMemo.loan_type && <div className="text-[#666]"><span className="text-[#999]">Type:</span> {investorMemo.loan_type}</div>}
              {investorMemo.term && <div className="text-[#666]"><span className="text-[#999]">Term:</span> {investorMemo.term}</div>}
              {investorMemo.structure && <div className="text-[#666]"><span className="text-[#999]">Structure:</span> {investorMemo.structure}</div>}
            </div>
          </div>
        )}

        {memo.summary && (
          <p className="text-xs text-[#666] leading-relaxed bg-[#FDFCF9] p-3 rounded-lg border border-[#f0ece4]">
            {memo.summary}
          </p>
        )}
      </div>

      {/* Sponsor Summary */}
      {Object.keys(sponsorSummary).length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="sponsor-summary">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888] mb-3 flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-amber-500" /> Sponsor Summary
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {sponsorSummary.name && (
              <div className="p-2.5 rounded-lg bg-[#FDFCF9] border border-[#f0ece4]">
                <div className="text-[9px] text-[#999] uppercase">Sponsor</div>
                <div className="text-xs font-bold text-[#333] mt-0.5">{sponsorSummary.name}</div>
              </div>
            )}
            {sponsorSummary.track_record && (
              <div className="p-2.5 rounded-lg bg-[#FDFCF9] border border-[#f0ece4]">
                <div className="text-[9px] text-[#999] uppercase">Track Record</div>
                <div className="text-xs font-bold text-[#333] mt-0.5">{sponsorSummary.track_record}</div>
              </div>
            )}
            {sponsorSummary.credit_profile && (
              <div className="p-2.5 rounded-lg bg-[#FDFCF9] border border-[#f0ece4]">
                <div className="text-[9px] text-[#999] uppercase">Credit Profile</div>
                <div className="text-xs font-bold text-[#333] mt-0.5">{sponsorSummary.credit_profile}</div>
              </div>
            )}
            {sponsorSummary.experience && (
              <div className="p-2.5 rounded-lg bg-[#FDFCF9] border border-[#f0ece4]">
                <div className="text-[9px] text-[#999] uppercase">Experience</div>
                <div className="text-xs font-bold text-[#333] mt-0.5">{sponsorSummary.experience}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Opportunity Highlights */}
      {highlights.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="opportunity-highlights">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888] mb-3 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> Opportunity Highlights
          </h4>
          <div className="space-y-2">
            {highlights.map((h, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                <div className="h-4 w-4 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                  <TrendingUp className="h-2.5 w-2.5 text-emerald-600" />
                </div>
                <p className="text-xs text-emerald-800">{h}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Downside Protection */}
      {protections.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="downside-protection">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888] mb-3 flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-blue-600" /> Downside Protection
          </h4>
          <div className="space-y-2">
            {protections.map((p, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-blue-50 border border-blue-100">
                <Shield className="h-3 w-3 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800">{p}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
